// Reusable Google Places Autocomplete input for address entry (BL-935 — merges
// admin's legacy google.maps.places.Autocomplete widget with bidder's
// AutocompleteSuggestion (Places API New) implementation, which becomes the
// base since Google deprecated the legacy widget). Uses a fully custom styled
// dropdown so the UI stays consistent with each app's design system. Falls
// back to a plain text input if the API key is missing or Places fails to load.
'use client';

import { useRef, useEffect, useState, useCallback, useContext } from 'react';
import { MapPin } from 'lucide-react';
import { Spinner } from './Spinner';
import { FieldSizeContext } from './FormField';
import clsx from 'clsx';

/** Structured address parsed from a Google Places result. */
export interface AddressResult {
    street: string;
    city: string;
    state: string;
    /**
     * County, with its administrative suffix stripped — `"Cass"`, not
     * `"Cass County"`. Empty string when Google returns no
     * `administrative_area_level_2`, which is normal outside the US and for
     * some city-states.
     *
     * Added by BL-1766 for the tax-rate drawer's location lookup, and
     * deliberately additive: every existing consumer ignores it, and the
     * item-location picker (BL-1767) reads the same field rather than
     * re-parsing the components itself.
     */
    county: string;
    postalCode: string;
    country: string;
    formattedAddress: string;
}

/**
 * Drop the administrative noun Google appends to a county name.
 *
 * Places returns `"Cass County"` / `"Orleans Parish"` / `"Nome Census Area"` in
 * both `longText` and `shortText`, but an operator entering a tax rate writes
 * `Cass`. Stripping here keeps every consumer from re-deriving the same rule.
 *
 * @param raw - The component text as Google returned it.
 * @returns The bare county name.
 */
function stripCountySuffix(raw: string): string {
    return raw
        .replace(/\s+(County|Parish|Borough|Census Area|Municipality|City and Borough)$/i, '')
        .trim();
}

/** Props for the AddressAutocomplete component. */
export interface AddressAutocompleteProps {
    value: string;
    onChange: (value: string) => void;
    onAddressSelect: (address: AddressResult) => void;
    placeholder?: string;
    error?: string;
    disabled?: boolean;
    className?: string;
    /** Accessible name for the input. Defaults to "Street address". */
    ariaLabel?: string;
    /** Automation handle on the street input (BL-2136); nothing without one. */
    testId?: string;
    /**
     * Restrict suggestions to these 2-letter ISO country codes (e.g. ['us']).
     * Omitted entirely by default — worldwide suggestions. Callers that want a
     * fixed restriction even when otherwise unlocked (e.g. bidder's
     * us/ca/mx-only address forms) pass it explicitly.
     */
    restrictToCountries?: string[];
    /**
     * Pre-validated brand color for the focus ring (a per-app dialect prop —
     * same pattern as `packages/ui/src/components/OTPInput.tsx`'s `brandColor`).
     * Callers are responsible for validating/falling back untrusted tenant
     * data before passing it here. When omitted, the ring falls back to the
     * `ring-primary` design token.
     */
    brandColor?: string;
}

// ── Google Places loader (singleton) ────────────────────────────────────────

let placesLibPromise: Promise<google.maps.PlacesLibrary> | null = null;
/** Tracks whether the Google Maps API reported an auth failure. */
let authFailed = false;
/** Listeners notified when auth fails (used to clean up mounted instances). */
const authFailureListeners: Set<() => void> = new Set();

// Listen for Google Maps auth failures (bad key, wrong referrer, etc.)
if (typeof window !== 'undefined') {
    // eslint-disable-next-line responsecast/no-response-type-erasure -- browser-global augmentation: attach the Google Maps SDK's gm_authFailure callback onto window; not a response DTO.
    (window as unknown as Record<string, unknown>).gm_authFailure = () => {
        console.warn('[AddressAutocomplete] Google Maps auth failure — falling back to plain input');
        authFailed = true;
        for (const listener of authFailureListeners) listener();
    };
}

/**
 * Loads the Google Places library once via @googlemaps/js-api-loader.
 * Returns null if the API key is not set or auth has previously failed.
 */
async function loadPlacesLibrary(): Promise<google.maps.PlacesLibrary | null> {
    const apiKey = process.env.NEXT_PUBLIC_GOOGLE_PLACES_API_KEY;
    if (!apiKey) {
        if (process.env.NODE_ENV === 'development') {
            console.warn('[AddressAutocomplete] NEXT_PUBLIC_GOOGLE_PLACES_API_KEY is not set — address autocomplete disabled, falling back to plain input.');
        }
        return null;
    }
    if (authFailed) return null;

    if (!placesLibPromise) {
        placesLibPromise = (async () => {
            const { setOptions, importLibrary } = await import('@googlemaps/js-api-loader');
            setOptions({ key: apiKey, v: 'weekly' });
            const lib = await importLibrary('places');
            return lib;
        })();
    }

    return placesLibPromise;
}

// ── Helper: parse address components (new Places API) ───────────────────────

/**
 * Parses a Places API (New) address component array into a structured AddressResult.
 * Uses the new API's `longText`/`shortText` fields (replaces legacy `long_name`/`short_name`).
 */
function parseAddressComponents(
    components: google.maps.places.AddressComponent[],
    formattedAddress: string,
): AddressResult {
    let streetNumber = '';
    let route = '';
    let city = '';
    let state = '';
    let county = '';
    let postalCode = '';
    let country = '';

    for (const c of components) {
        const type = c.types[0];
        if (type === 'street_number') streetNumber = c.longText ?? '';
        else if (type === 'route') route = c.longText ?? '';
        else if (type === 'locality') city = c.longText ?? '';
        else if (type === 'sublocality_level_1' && !city) city = c.longText ?? '';
        else if (type === 'administrative_area_level_1') state = c.shortText ?? '';
        // A county component's FIRST type is `administrative_area_level_2`, so
        // the `types[0]` keying above reaches it unchanged.
        else if (type === 'administrative_area_level_2')
            county = stripCountySuffix(c.longText ?? '');
        else if (type === 'postal_code') postalCode = c.longText ?? '';
        else if (type === 'country') country = c.longText ?? '';
    }

    const street = streetNumber ? `${streetNumber} ${route}` : route;

    return { street, city, state, county, postalCode, country, formattedAddress };
}

// ── Component ───────────────────────────────────────────────────────────────

/**
 * Address input with Google Places Autocomplete.
 * Uses the AutocompleteSuggestion programmatic API with a custom-styled dropdown
 * that matches each app's design system (no Google-branded widget chrome).
 * Emits a structured AddressResult when the user selects a suggestion.
 * Falls back to a plain text input when the Places API is unavailable.
 */
export function AddressAutocomplete({
    value,
    onChange,
    onAddressSelect,
    placeholder = 'Search for an address…',
    error,
    disabled = false,
    className,
    ariaLabel = 'Street address',
    restrictToCountries,
    brandColor,
    testId,
}: AddressAutocompleteProps) {
    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // BL-2754: a phone sheet sets the touch field size.
    const touch = useContext(FieldSizeContext) === 'touch';
    const [loading, setLoading] = useState(true);
    const [placesAvailable, setPlacesAvailable] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [suggestions, setSuggestions] = useState<google.maps.places.AutocompleteSuggestion[]>([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);

    // No default region set — an omitted `restrictToCountries` means worldwide
    // suggestions (admin's original unlocked behavior). Callers that want a
    // fixed restriction even when unlocked (e.g. bidder's us/ca/mx forms) pass
    // it explicitly; `AddressFormFields`' `lockedCountry` mode also derives one.
    const regionCodes = restrictToCountries;
    const regionCodesKey = (regionCodes ?? []).join(',');

    // Keep stable refs to latest callbacks to avoid stale closures
    const onAddressSelectRef = useRef(onAddressSelect);
    const onChangeRef = useRef(onChange);
    useEffect(() => { onAddressSelectRef.current = onAddressSelect; }, [onAddressSelect]);
    useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

    // Clear dropdown when value is cleared externally (e.g. form reset)
    useEffect(() => {
        if (!value) {
            setSuggestions([]);
            setShowDropdown(false);
        }
    }, [value]);

    // Load Places library
    useEffect(() => {
        let cancelled = false;

        const handleAuthFailure = () => {
            if (cancelled) return;
            setPlacesAvailable(false);
            setLoading(false);
        };

        if (authFailed) {
            setLoading(false);
            return () => { cancelled = true; };
        }

        authFailureListeners.add(handleAuthFailure);

        (async () => {
            try {
                const places = await loadPlacesLibrary();
                if (cancelled || !places) {
                    setLoading(false);
                    return;
                }
                if (authFailed) {
                    handleAuthFailure();
                    return;
                }
                if (!cancelled) {
                    setPlacesAvailable(true);
                    setLoading(false);
                }
            } catch (err) {
                const message = err instanceof Error ? err.message : String(err);
                if (message.includes('are blocked') || message.includes('REQUEST_DENIED')) {
                    console.warn(
                        '[AddressAutocomplete] Places API is blocked for this API key — falling back to plain text input.',
                    );
                } else {
                    console.error('[AddressAutocomplete] Failed to load Google Places:', err);
                }
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
            authFailureListeners.delete(handleAuthFailure);
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, []); // Run once on mount — loadPlacesLibrary is a module singleton, no reactive deps needed

    // Fetch autocomplete suggestions via the programmatic API
    const fetchSuggestions = useCallback(async (input: string) => {
        if (!input.trim()) {
            setSuggestions([]);
            setShowDropdown(false);
            return;
        }
        try {
            const { suggestions: results } =
                await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
                    input,
                    // 'street_address' returns precise street-level addresses with full components.
                    // 'route' covers roads/highways for partial input like "123 Main".
                    // Both are valid Table A types in Places API (New); 'address' (legacy) is not.
                    includedPrimaryTypes: ['street_address', 'route'],
                    // Omitted entirely (not `undefined`-valued) when no restriction is
                    // requested — worldwide suggestions, matching admin's original
                    // unlocked behavior.
                    ...(regionCodes && regionCodes.length > 0 ? { includedRegionCodes: regionCodes } : {}),
                });
            setSuggestions(results);
            setShowDropdown(results.length > 0);
            setActiveIndex(-1);
        } catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            // "are blocked" = API key does not have AutocompletePlaces enabled.
            // Disable the suggestions feature permanently so we don't log on every keystroke.
            // The input remains fully usable as a plain text field.
            if (message.includes('are blocked') || message.includes('REQUEST_DENIED')) {
                console.warn(
                    '[AddressAutocomplete] Places.AutocompletePlaces is blocked for this API key. ' +
                    'Ensure "Places API (New)" is enabled in Google Cloud Console and listed under ' +
                    'the API key\'s API restrictions. Falling back to plain text input.',
                );
                setPlacesAvailable(false);
            } else {
                console.error('[AddressAutocomplete] Failed to fetch suggestions:', err);
            }
            setSuggestions([]);
            setShowDropdown(false);
        }
    }, [regionCodesKey]);

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const val = e.target.value;
            onChange(val);
            if (!placesAvailable) return;
            if (debounceRef.current) clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => fetchSuggestions(val), 200);
        },
        [onChange, placesAvailable, fetchSuggestions],
    );

    const handleSuggestionSelect = useCallback(
        async (suggestion: google.maps.places.AutocompleteSuggestion) => {
            setShowDropdown(false);
            setSuggestions([]);
            const pred = suggestion.placePrediction;
            if (!pred) return;
            try {
                const place = pred.toPlace();
                await place.fetchFields({ fields: ['addressComponents', 'formattedAddress'] });
                const { addressComponents, formattedAddress } = place;
                if (addressComponents && formattedAddress) {
                    const result = parseAddressComponents(addressComponents, formattedAddress);
                    // onAddressSelect updates ALL fields (street included) via the parent's onChange.
                    // Do NOT call onChangeRef here — it would spread the stale value prop and overwrite
                    // city/state/postalCode/country with empty strings before the parent state update lands.
                    onAddressSelectRef.current(result);
                }
            } catch (err) {
                console.error('[AddressAutocomplete] Failed to fetch place details:', err);
                // Re-open the dropdown so the user can retry selecting a suggestion
                // instead of silently leaving the form in an empty state.
                setShowDropdown(true);
            }
        },
        [],
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (!showDropdown || suggestions.length === 0) return;
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setActiveIndex((i) => Math.max(i - 1, -1));
            } else if (e.key === 'Enter' && activeIndex >= 0) {
                e.preventDefault();
                void handleSuggestionSelect(suggestions[activeIndex]);
            } else if (e.key === 'Escape') {
                setShowDropdown(false);
                setActiveIndex(-1);
            }
        },
        [showDropdown, suggestions, activeIndex, handleSuggestionSelect],
    );

    // Close dropdown on outside click
    useEffect(() => {
        if (!showDropdown) return;
        const handleClickOutside = (e: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(e.target as Node) &&
                inputRef.current &&
                !inputRef.current.contains(e.target as Node)
            ) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showDropdown]);

    return (
        <div className={clsx('relative', className)}>
            {/* BUG-1871: the icon centers on this box, so it holds only the input.
                The error line lives outside it; inside, it made the box taller and
                pushed the icon below the input's middle. */}
            <div className="relative">
                {loading ? (
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 z-10 pointer-events-none">
                        <Spinner size={16} accentColor="#a1a1aa" />
                    </span>
                ) : (
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 z-10 pointer-events-none" />
                )}

                <input
                    ref={inputRef}
                    type="text"
                    value={value}
                    onChange={handleChange}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    onKeyDown={handleKeyDown}
                    disabled={disabled}
                    placeholder={placesAvailable ? placeholder : 'Enter street address'}
                    autoComplete="off"
                    aria-label={ariaLabel}
                    data-testid={testId}
                    aria-autocomplete="list"
                    aria-expanded={showDropdown && isFocused && suggestions.length > 0}
                    aria-haspopup="listbox"
                    className={clsx(
                        'w-full pl-9 pr-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent',
                        // BL-2754: on a phone sheet the sheet field size; elsewhere the 44px row.
                        touch ? 'min-h-12 py-2 text-body-lg' : 'py-3 text-sm',
                        error ? 'border-red-400 ring-2 ring-red-400' : 'border-zinc-300',
                        disabled && 'opacity-50 bg-zinc-50 cursor-not-allowed',
                    )}
                    style={
                        isFocused && !error && brandColor
                            ? ({ '--tw-ring-color': brandColor } as React.CSSProperties)
                            : undefined
                    }
                />
            </div>

            {/* Suggestions dropdown — only while the input holds focus (BUG-2040): results that land
                after the operator has moved on would otherwise open over the next field. A pick keeps
                focus (its mousedown is prevented), so picking is unaffected. */}
            {showDropdown && isFocused && suggestions.length > 0 && (
                <div
                    ref={dropdownRef}
                    role="listbox"
                    className="absolute z-50 left-0 right-0 mt-1 bg-white border border-zinc-200 rounded-lg shadow-lg overflow-hidden"
                >
                    {suggestions.map((suggestion, index) => {
                        const pred = suggestion.placePrediction;
                        if (!pred) return null;
                        const mainText = pred.mainText?.text ?? '';
                        const secondaryText = pred.secondaryText?.text ?? '';
                        return (
                            <button
                                key={pred.placeId}
                                type="button"
                                role="option"
                                aria-selected={index === activeIndex}
                                className={clsx(
                                    'w-full text-left px-4 py-3 text-sm flex items-start gap-3 transition-colors',
                                    index === activeIndex ? 'bg-zinc-50' : 'hover:bg-zinc-50',
                                    index > 0 && 'border-t border-zinc-100',
                                )}
                                onMouseDown={(e) => {
                                    // mousedown fires before blur — prevent input losing focus before selection
                                    e.preventDefault();
                                    void handleSuggestionSelect(suggestion);
                                }}
                            >
                                <MapPin className="w-4 h-4 text-zinc-400 mt-0.5 flex-shrink-0" />
                                <span className="min-w-0">
                                    <span className="font-medium text-zinc-900">{mainText}</span>
                                    {secondaryText && (
                                        <span className="text-zinc-500 ml-1">{secondaryText}</span>
                                    )}
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}

            {error && <p className={touch ? 'mt-1 text-body text-red-600' : 'mt-1 text-sm text-red-500'}>{error}</p>}
        </div>
    );
}
